import { ContractorFormData, FilterParams } from './types';
import { ContractorService, CreateContractorRequest } from '../../services/contractor.service';
import { authService } from '../../services/auth.service';
import { geocodeAddress } from '../../utils/geocodeAddress';

// Debounce function for search
export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export { geocodeAddress as getCoordinatesFromAddress };

// Function to format the form data for API request
export const formatFormDataForAPI = (formData: ContractorFormData): CreateContractorRequest => {
  // Build the contractor trades array
  const contractorTrades = formData.multiTrade 
    ? formData.trades.map(tradeId => ({
        trade: tradeId,
        unionType: formData.tradeUnionTypes[tradeId] || 'union'
      }))
    : formData.singleTrade 
      ? [{
          trade: formData.singleTrade,
          unionType: formData.tradeUnionTypes[formData.singleTrade] || 'union'
        }]
      : [];

  // Format addresses with head office and sub-offices
  // Filter out sub-offices with empty provinces
  const validSubOffices = formData.subOffices.filter(office => office.province);

  const addresses = [
    {
      addressType: 'head-office',
      address: formData.address,
      city: formData.city,
      province: formData.province,
      postalCode: formData.postalCode,
      coordinates: formData.coordinates || [0, 0]
    },
    ...validSubOffices.map((office) => ({
      addressType: 'sub-office',
      address: office.address,
      city: office.city,
      province: office.province,
      postalCode: office.postalCode,
      coordinates: office.coordinates || [0, 0]
    }))
  ];

  // Get current user from auth service
  const currentUser = authService.getUser();
  const userId = currentUser?.id || '';

  // Format the data according to the API requirements
  return {
    name: formData.name,
    contractorSectors: formData.type,
    contractorUnionType: formData.unionType,
    email: formData.email,
    mobileNumber: formData.phoneNumber,
    isSignatory: formData.isSignificant === 'yes',
    localUnions: formData.isSignificant === 'yes' ? formData.localUnions : [],
    isMultiTrade: formData.multiTrade,
    contractorTrades,
    status: formData.status,
    address: addresses,
    additionalNotes: formData.note,
    userId: userId
  };
};

// Function to check if form data has changed
export const hasFormDataChanged = (originalData: any, currentData: any) => {
  // Helper function to compare objects deeply
  const isEqual = (obj1: any, obj2: any): boolean => {
    if (obj1 === obj2) return true;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;
    if (obj1 === null || obj2 === null) return obj1 === obj2;
    
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!isEqual(obj1[key], obj2[key])) return false;
    }
    
    return true;
  };

  // Compare relevant fields
  const relevantFields = [
    'name', 'ownerName', 'email', 'phoneNumber', 'type', 'unionType',
    'isSignificant', 'localUnions', 'multiTrade', 'trades', 'singleTrade',
    'tradeUnionTypes', 'status', 'note', 'address', 'city', 'province',
    'postalCode', 'subOffices'
  ];

  for (const field of relevantFields) {
    if (!isEqual(originalData[field], currentData[field])) {
      return true;
    }
  }

  return false;
};

// Function to fetch contractors with filters
export const fetchContractors = async (
  params: {
    currentPage: number;
    searchTerm: string;
    pageSize: number;
    sortConfig: { key: string; direction: 'asc' | 'desc' | null };
  },
  callbacks: {
    setLoading: (loading: boolean) => void;
    setContractors: (contractors: any[]) => void;
    setTotalItems: (total: number) => void;
  }
) => {
  const { currentPage, searchTerm, pageSize, sortConfig } = params;
  const { setLoading, setContractors, setTotalItems } = callbacks;

  setLoading(true);
  try {
    // Check if sorting is applied
    const isSort = sortConfig.direction !== null;
    
    // Prepare filter params with proper typing
    const filterParams: FilterParams = {
      search: searchTerm,
      page: currentPage,
      limit: pageSize
    };
    
    // Add sorting params if sorting is applied
    if (isSort && sortConfig.direction) {
      // Map 'name' to 'contractor' for the API
      if (sortConfig.key === 'name') {
        filterParams.sortBy = 'contractor';
      } else if (sortConfig.key === 'contractorType') {
        filterParams.sortBy = 'sector';
      } else if (sortConfig.key === 'ownerName') {
        filterParams.sortBy = 'ownerName';
      } else if (sortConfig.key === 'status') {
        filterParams.sortBy = 'status';
      } else if (sortConfig.key === 'createdBy') {
        filterParams.sortBy = 'createdBy';
      } else {
        filterParams.sortBy = sortConfig.key;
      }
      filterParams.sortOrder = sortConfig.direction;
    }

    

    // Call the API
    const response = await ContractorService.getContractorsWithFilters(filterParams);

    // Helper function to map API contractor data to display format
    const mapContractorData = (contractor: any) => {
      return {
        ...contractor,
        id: contractor.id || '',
        name: contractor.name || '',
        ownerName: contractor.ownerName || '',
        createdBy: contractor.createdBy || null,
        createdAt: contractor.createdAt || '',
        type: contractor.contractorSectors && Array.isArray(contractor.contractorSectors) 
          ? contractor.contractorSectors.map((cs: any) => cs.sector?.id || cs.sectorId).filter(Boolean)
          : [],
        contractorUnionType: contractor.contractorUnionType || '',
        status: contractor.status
          ? contractor.status.charAt(0).toUpperCase() + contractor.status.slice(1)
          : 'Unknown',
        cellNumber: contractor.cellNumber || '',
        mobileNumber: contractor.mobileNumber || contractor.cellNumber || '',
        address: contractor.address && Array.isArray(contractor.address) && contractor.address.length > 0
          ? contractor.address
          : null,
      };
    };
    
    let mappedContractors: any[] = [];
    let total = 0;
    
    // Handle response based on its structure
    if (Array.isArray(response)) {
      mappedContractors = response.map(mapContractorData);
      total = response.length;
    } 
    // For responses with pagination info
    else if (response && typeof response === 'object') {
      // Check if it has a data property and matches PaginatedContractorResponse
      if ('data' in response && 'totalCount' in response) {
        mappedContractors = response.data.map(mapContractorData);
        total = response.totalCount;
      } else {
        // This fallback should never be reached with typed responses
        console.error('Unexpected response format:', response);
        mappedContractors = [];
        total = 0;
      }
    }
    
    // Ensure we're only showing pageSize records maximum
    if (mappedContractors.length > pageSize) {
      console.warn(`API returned ${mappedContractors.length} records, limiting to ${pageSize}`);
      mappedContractors = mappedContractors.slice(0, pageSize);
    }
    
    setContractors(mappedContractors);
    setTotalItems(total);
  } catch (error) {
    console.error('❌ Failed to fetch contractors:', error);
    setContractors([]);
  } finally {
    setLoading(false);
  }
};
